import { useCallback, useState } from 'react';

/**
 * The browser keys of the diagram screens' view preferences. They are one person's view and never
 * part of a document, so they stay in this browser and nothing else sees them.
 */
export const DIAGRAM_PREFERENCE_KEYS = {
  grid: 'cti.diagrams.grid',
  chatWidth: 'cti.diagrams.chatWidth',
  chatCollapsed: 'cti.diagrams.chatCollapsed',
} as const;

function read<T>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | null,
): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (parse(raw) ?? fallback);
  } catch {
    return fallback;
  }
}

/**
 * A value kept in `localStorage`. A browser that refuses storage (a private window, blocked site
 * data) still gets a working value for the session; it is just not remembered.
 */
export function useStoredPreference<T>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | null,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => read(key, fallback, parse));
  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, String(next));
      } catch {
        return;
      }
    },
    [key],
  );
  return [value, update];
}

/** `"true"` and `"false"`, as `String(boolean)` writes them. */
export function parseBoolean(raw: string): boolean | null {
  return raw === 'true' ? true : raw === 'false' ? false : null;
}

/** A finite number, as `String(number)` writes it. */
export function parseNumber(raw: string): number | null {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}
